import axios from "axios";
import PropTypes from "prop-types";
import Slider from "rc-slider";
import React, { useState, useEffect } from "react";
import "rc-slider/assets/index.css";

import { notifyUser } from "../utils/notifyUser";

function InvestmentCalculatorModal({
    isOpen,
    onClose,
    investorData,
    campaignId,
    onProceed,
}) {
    const [formData, setFormData] = useState({
        netWorth: "",
        yearlyIncome: "",
        priorRegCfAmount: "",
    });
    const [investmentAmount, setInvestmentAmount] = useState("");
    const [unitPrice, setUnitPrice] = useState(null);

    // Fetch unit price for this campaign
    useEffect(() => {
        if (!campaignId) return;
        const fetchUnitPrice = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/view/${campaignId}`,
                    {
                        method: "GET",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                    },
                );
                const data = await response.json();
                if (data && data.unit_price) {
                    setUnitPrice(Number(data.unit_price));
                }
            } catch (err) {
                setUnitPrice(null);
            }
        };
        fetchUnitPrice();
    }, [campaignId]);

    // Compute shares
    let shares = null;
    if (unitPrice && investmentAmount) {
        const amt = parseFloat(investmentAmount);
        if (!isNaN(amt) && unitPrice > 0) {
            shares = amt / unitPrice;
        }
    }
    const [errors, setErrors] = useState({});
    const [annualLimit, setAnnualLimit] = useState(0);
    const [remainingLimit, setRemainingLimit] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [campaignMinimum, setCampaignMinimum] = useState(2000);

    // Initialize form with investor data
    useEffect(() => {
        if(investorData && isOpen) {
            setFormData({
                netWorth: investorData.net_worth
                    ? investorData.net_worth.toString()
                    : "",
                yearlyIncome: investorData.yearly_income
                    ? investorData.yearly_income.toString()
                    : "",
                priorRegCfAmount: investorData.prior_reg_cf_amount
                    ? investorData.prior_reg_cf_amount.toString()
                    : "0",
            });
        }
    }, [investorData, isOpen]);

    // Fetch campaign minimum investment amount
    useEffect(() => {
        if(!campaignId) return;

        const fetchCampaignMinimum = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/view/${campaignId}`,
                    {
                        method: "GET",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                    },
                );
                const data = await response.json();
                if(data.investment_minimum) {
                    setCampaignMinimum(data.investment_minimum);
                }
            } catch(error) {
                console.error("Error fetching campaign minimum:", error);
                // Keep default value if fetch fails
            }
        };

        fetchCampaignMinimum();
    }, [campaignId]);

    // Calculate annual investment limit
    useEffect(() => {
        const netWorth = parseInt(formData.netWorth) || 0;
        const yearlyIncome = parseInt(formData.yearlyIncome) || 0;

        const greater = Math.max(yearlyIncome, netWorth);
        if(netWorth < 124000 || yearlyIncome < 124000) {
            const fivePercent = greater * 0.05;
            setAnnualLimit(Math.max(2500, fivePercent));
        } else {
            const tenPercent = greater * 0.1;
            setAnnualLimit(Math.min(124000, tenPercent));
        }
    }, [formData.netWorth, formData.yearlyIncome]);

    // Calculate remaining limit
    useEffect(() => {
        const prior = parseFloat(formData.priorRegCfAmount || 0) || 0;
        const remaining = Math.max(annualLimit - prior, 0);
        setRemainingLimit(remaining);
    }, [
        formData.yearlyIncome,
        formData.netWorth,
        formData.priorRegCfAmount,
        annualLimit,
    ]);

    // Inject custom slider styles
    useEffect(() => {
        const styleId = "slider-styles-modal";
        if(!document.getElementById(styleId)) {
            const style = document.createElement("style");
            style.id = styleId;
            style.textContent = `
        .rc-slider {
          margin: 16px 0;
          padding-bottom: 30px;
        }

        .rc-slider-track {
          background-color: var(--navy);
          height: 4px;
        }

        .rc-slider-rail {
          background-color: #e5e7eb;
          height: 4px;
        }

        .rc-slider-handle {
          width: 16px;
          height: 16px;
          margin-top: -6px;
          background-color: var(--navy);
          border: none;
          box-shadow: 0 2px 4px rgb(var(--navy-rgb) / 0.2);
          opacity: 1;
        }

        .rc-slider-handle:hover {
          box-shadow: 0 2px 8px rgb(var(--navy-rgb) / 0.3);
        }

        .rc-slider-handle-dragging {
          box-shadow: 0 2px 8px rgb(var(--navy-rgb) / 0.3);
        }

        .rc-slider-mark {
          font-size: 12px;
          color: #6b7280;
        }

        .rc-slider-mark-text {
          color: #6b7280;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
        }

        .rc-slider-step {
          background: transparent;
        }
      `;
            document.head.appendChild(style);
        }
    }, []);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const validateFields = () => {
        const newErrors = {};

        if(!formData.netWorth || formData.netWorth === "") {
            newErrors.netWorth = "Net worth is required";
        }

        if(!formData.yearlyIncome || formData.yearlyIncome === "") {
            newErrors.yearlyIncome = "Annual income is required";
        }

        const priorVal = formData.priorRegCfAmount;
        if(priorVal === undefined || priorVal === "") {
            newErrors.priorRegCfAmount =
                "Prior 12-month Reg CF amount is required";
        } else {
            const num = parseFloat(priorVal);
            if(isNaN(num) || num < 0) {
                newErrors.priorRegCfAmount =
                    "Enter a valid non-negative number";
            } else if(annualLimit > 0 && num > annualLimit) {
                newErrors.priorRegCfAmount =
                    "Amount exceeds your annual allowable limit";
            }
        }

        // Validate investment amount
        if(!investmentAmount || investmentAmount === "") {
            newErrors.investmentAmount = "Investment amount is required";
        } else {
            const amount = parseFloat(investmentAmount);
            if(isNaN(amount) || amount <= 0) {
                newErrors.investmentAmount = "Enter a valid investment amount";
            } else if(amount < campaignMinimum) {
                newErrors.investmentAmount = `Minimum investment is $${campaignMinimum.toLocaleString()}. You entered $${amount}`;
            } else if(amount > remainingLimit) {
                newErrors.investmentAmount = `Amount exceeds your remaining limit of $${remainingLimit.toLocaleString()}`;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));

        if(errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    const handleSaveAndProceed = async () => {
        if(!validateFields()) {
            return;
        }

        setIsSaving(true);
        try {
            const userId = investorData?.user_id;
            if(!userId) {
                notifyUser(null, "error", "User information not available", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }

            // Update investor data in database
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/investors/${userId}`,
                {
                    net_worth: parseFloat(formData.netWorth),
                    yearly_income: parseFloat(formData.yearlyIncome),
                    prior_reg_cf_amount: parseFloat(formData.priorRegCfAmount),
                },
                {
                    withCredentials: true,
                },
            );

            if(response.status === 200) {
                notifyUser(null, "success", "Investment limits updated successfully", "toast", null, null, null, null, null, false, false, "top-center");
                // Call the onProceed callback to continue with investment
                if(onProceed) {
                    onProceed({
                        netWorth: parseFloat(formData.netWorth),
                        yearlyIncome: parseFloat(formData.yearlyIncome),
                        priorRegCfAmount: parseFloat(formData.priorRegCfAmount),
                        investmentAmount: parseFloat(investmentAmount),
                        remainingLimit,
                    });
                }
                onClose();
            }
        } catch(error) {
            console.error("Error updating investor data:", error);
            notifyUser(null, "error", "Failed to update investment limits. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setIsSaving(false);
        }
    };

    if(!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-navy">
                        Let&apos;s verify your investment limits
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 text-2xl"
                    >
                        &times;
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-8">
                    {/* Net Worth */}
                    <div>
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
                            <label className="text-sm font-semibold text-[#23304a]">
                                What is your net worth?
                            </label>
                            <div className="relative shrink-0">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-[#6b7a8f] font-medium sm:text-sm">$</span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="0"
                                    value={formData.netWorth}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(
                                            /\D/g,
                                            "",
                                        );
                                        handleInputChange("netWorth", value);
                                    }}
                                    className="w-full sm:w-40 pl-7 pr-3 py-2.5 bg-white border border-[#e3eaf3] rounded-lg text-[#23304a] text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy hover:border-[#ced6e0] shadow-sm"
                                />
                            </div>
                        </div>

                        {/* Slider for net worth */}
                        <div className="mb-4 px-1">
                            <Slider
                                min={0}
                                max={1000000}
                                step={50000}
                                value={parseInt(formData.netWorth) || 0}
                                onChange={(value) =>
                                    handleInputChange(
                                        "netWorth",
                                        value.toString(),
                                    )
                                }
                                marks={{
                                    50000: "$50k",
                                    150000: "$100k",
                                    500000: "$500k",
                                    1000000: "$1M",
                                }}
                                trackStyle={{
                                    backgroundColor: "var(--navy)",
                                    height: 4,
                                }}
                                railStyle={{
                                    backgroundColor: "#e5e7eb",
                                    height: 4,
                                }}
                                handleStyle={{
                                    borderColor: "var(--navy)",
                                    backgroundColor: "var(--navy)",
                                    boxShadow:
                                        "0 2px 4px rgb(var(--navy-rgb) / 0.2)",
                                    width: 16,
                                    height: 16,
                                    marginTop: -6,
                                }}
                            />
                        </div>

                        {errors.netWorth && (
                            <p className="text-red-500 text-sm">
                                {errors.netWorth}
                            </p>
                        )}
                    </div>

                    {/* Annual Income */}
                    <div>
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
                            <label className="text-sm font-semibold text-[#23304a]">
                                What is your annual income?
                            </label>
                            <div className="relative shrink-0">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-[#6b7a8f] font-medium sm:text-sm">$</span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="0"
                                    value={formData.yearlyIncome}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(
                                            /\D/g,
                                            "",
                                        );
                                        handleInputChange(
                                            "yearlyIncome",
                                            value,
                                        );
                                    }}
                                    className="w-full sm:w-40 pl-7 pr-3 py-2.5 bg-white border border-[#e3eaf3] rounded-lg text-[#23304a] text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy hover:border-[#ced6e0] shadow-sm"
                                />
                            </div>
                        </div>

                        {/* Slider for annual income */}
                        <div className="mb-4 px-1">
                            <Slider
                                min={0}
                                max={300000}
                                step={5000}
                                value={parseInt(formData.yearlyIncome) || 0}
                                onChange={(value) =>
                                    handleInputChange(
                                        "yearlyIncome",
                                        value.toString(),
                                    )
                                }
                                marks={{
                                    50000: "$50k",
                                    100000: "$100k",
                                    200000: "$200k",
                                    300000: "$300k+",
                                }}
                                trackStyle={{
                                    backgroundColor: "var(--navy)",
                                    height: 4,
                                }}
                                railStyle={{
                                    backgroundColor: "#e5e7eb",
                                    height: 4,
                                }}
                                handleStyle={{
                                    borderColor: "var(--navy)",
                                    backgroundColor: "var(--navy)",
                                    boxShadow:
                                        "0 2px 4px rgb(var(--navy-rgb) / 0.2)",
                                    width: 16,
                                    height: 16,
                                    marginTop: -6,
                                }}
                            />
                        </div>

                        {errors.yearlyIncome && (
                            <p className="text-red-500 text-sm">
                                {errors.yearlyIncome}
                            </p>
                        )}
                    </div>

                    {/* Prior 12-month Reg CF Investments */}
                    <div>
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
                            <label className="text-sm font-semibold text-[#23304a] leading-relaxed flex-1">
                                Total amount you have already invested in any
                                Regulation Crowdfunding offerings in the last 12
                                months (all portals).
                            </label>
                            <div className="relative shrink-0">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-[#6b7a8f] font-medium sm:text-sm">$</span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="0"
                                    value={formData.priorRegCfAmount || ""}
                                    onChange={(e) => {
                                        let value = e.target.value;
                                        value = value.replace(/[^0-9.]/g, "");
                                        value = value.replace(/^0+(?=\d)/, "");
                                        handleInputChange(
                                            "priorRegCfAmount",
                                            value,
                                        );
                                    }}
                                    className={`w-full sm:w-40 pl-7 pr-3 py-2.5 bg-white border rounded-lg text-[#23304a] text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-navy/20 hover:border-[#ced6e0] shadow-sm ${
                                        errors.priorRegCfAmount
                                            ? "border-red-500 focus:ring-red-500"
                                            : "border-[#e3eaf3] focus:border-navy"
                                    }`}
                                />
                            </div>
                        </div>
                        {errors.priorRegCfAmount && (
                            <p className="text-red-500 text-sm mt-1">
                                {errors.priorRegCfAmount}
                            </p>
                        )}
                    </div>

                    {/* Investment Amount */}
                    <div className="pt-4">
                        {/* Read-only minimum investment field (label left, box right) */}
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
                            <label className="text-sm font-semibold text-[#23304a] leading-relaxed flex-1">
                                Minimum Investment
                            </label>
                            <div className="relative shrink-0">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-[#6b7a8f] font-medium sm:text-sm">$</span>
                                </div>
                                <input
                                    type="text"
                                    readOnly
                                    value={
                                        campaignMinimum != null && campaignMinimum !== ""
                                            ? `$${Number(campaignMinimum).toLocaleString()}`
                                            : "—"
                                    }
                                    className="w-full sm:w-40 pl-7 pr-3 py-2.5 bg-white border rounded-lg text-[#23304a] text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-navy/20 hover:border-[#ced6e0] shadow-sm border-[#e3eaf3] focus:border-navy"
                                />
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
                            <label className="text-sm font-semibold text-[#23304a] flex-1">
                                How much do you want to invest in this campaign?
                            </label>
                            <div className="relative shrink-0">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-[#6b7a8f] font-medium sm:text-sm">$</span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="0"
                                    value={investmentAmount}
                                    onChange={(e) => {
                                        let value = e.target.value;
                                        value = value.replace(/[^0-9.]/g, "");
                                        value = value.replace(/^0+(?=\d)/, "");
                                        setInvestmentAmount(value);
                                        if(errors.investmentAmount) {
                                            setErrors((prev) => ({
                                                ...prev,
                                                investmentAmount: undefined,
                                            }));
                                        }
                                    }}
                                    className={`w-full sm:w-40 pl-7 pr-3 py-2.5 bg-white border rounded-lg text-[#23304a] text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-navy/20 hover:border-[#ced6e0] shadow-sm ${
                                        errors.investmentAmount
                                            ? "border-red-500 focus:ring-red-500"
                                            : "border-[#e3eaf3] focus:border-navy"
                                    }`}
                                />
                            </div>
                        </div>
                        {errors.investmentAmount && (
                            <p className="text-red-500 text-sm mt-1">
                                {errors.investmentAmount}
                            </p>
                        )}
                         {/* Shares info below input */}
                        {unitPrice && shares && shares > 0 && (
                            <div className="font-poppins mt-2 text-left sm:text-right text-xs text-[#6b7a8f]">
                                You are buying <span className="font-semibold text-[#23304a]">{shares.toLocaleString(undefined, { maximumFractionDigits: 4 })}</span> shares of this campaign.
                            </div>
                        )}
                    </div>

                    {/* Investment Limit Summary */}
                    {formData.netWorth && formData.yearlyIncome && (
                        <div className="bg-blue-50 p-6 rounded-lg">
                            <h3 className="font-semibold text-navy mb-2">
                                You are legally allowed to invest
                            </h3>
                            <div className="text-3xl font-bold text-navy mb-3">
                                {formatCurrency(annualLimit)} / yr
                            </div>
                            <div className="text-lg font-semibold text-navy mb-3">
                                Remaining This Period:{" "}
                                {formatCurrency(remainingLimit)}
                            </div>

                            {/* Why Tooltip */}
                            <p className="text-sm text-blue-700 mb-4">
                                <span
                                    className="underline cursor-pointer relative inline-block"
                                    onMouseEnter={() => setShowTooltip(true)}
                                    onMouseLeave={() => setShowTooltip(false)}
                                >
                                    Why?
                                    {showTooltip && (
                                        <div className="absolute left-full ml-2 p-4 bg-navy text-white text-sm rounded-lg shadow-lg w-80 z-10 whitespace-normal">
                                            <h4 className="font-semibold mb-2">
                                                Federal law sets the investing
                                                limits for Regulation
                                                Crowdfunding
                                            </h4>
                                            <ul className="text-xs space-y-1">
                                                <li>
                                                    • Everyone can invest at
                                                    least $2,500—no matter your
                                                    income or net worth
                                                </li>
                                                <li>
                                                    • If either your income or
                                                    net worth is under $124K,
                                                    you can invest up to 5% of
                                                    the greater number
                                                </li>
                                                <li>
                                                    • If both your income and
                                                    net worth are $124K or more,
                                                    you can invest up to 10% of
                                                    the greater number, with a
                                                    maximum cap of $124,000
                                                </li>
                                                <li>
                                                    • Investment limits are for
                                                    every 12-month period
                                                </li>
                                                <li>
                                                    • Every investment is a
                                                    Regulation Crowdfunding
                                                    offering counts towards
                                                    these federal limits
                                                </li>
                                                <li>
                                                    • There is no limit for
                                                    accredited investors
                                                </li>
                                            </ul>
                                        </div>
                                    )}
                                </span>
                            </p>

                            {/* Summary */}
                            <div className="mt-4 pt-4 border-t border-blue-200 text-sm text-gray-700">
                                Based on your net worth of{" "}
                                <span className="font-semibold text-navy">
                                    {formatCurrency(formData.netWorth)}
                                </span>{" "}
                                and annual income of{" "}
                                <span className="font-semibold text-navy">
                                    {formatCurrency(formData.yearlyIncome)}
                                </span>
                                , you can invest up to{" "}
                                <span className="font-semibold text-navy">
                                    {formatCurrency(annualLimit)}
                                </span>{" "}
                                per year. After accounting for prior Reg CF
                                investments of{" "}
                                <span className="font-semibold text-navy">
                                    {formatCurrency(
                                        parseFloat(
                                            formData.priorRegCfAmount || 0,
                                        ),
                                    )}
                                </span>
                                , you have{" "}
                                <span className="font-semibold text-navy">
                                    {formatCurrency(remainingLimit)}
                                </span>{" "}
                                remaining this period.
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer - Buttons */}
                <div className="flex justify-between gap-4 p-6 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-100 transition-all"
                    >
                        Cancel
                    </button>
                    {(() => {
                        const investmentAmountNum =
                            parseFloat(investmentAmount) || 0;
                        const isValidAmount =
                            investmentAmountNum >= campaignMinimum;
                        const isDisabled = isSaving || !isValidAmount;

                        return (
                            <button
                                onClick={handleSaveAndProceed}
                                disabled={isDisabled}
                                className={`flex-1 px-6 py-3 rounded-lg font-semibold text-white transition-all ${
                                    isDisabled
                                        ? "bg-gray-400 cursor-not-allowed opacity-60"
                                        : "bg-navy hover:bg-navy-2"
                                }`}
                                title={
                                    !isValidAmount
                                        ? `Minimum investment is $${campaignMinimum.toLocaleString()}`
                                        : ""
                                }
                            >
                                {isSaving
                                    ? "Saving..."
                                    : "Click Here to Proceed"}
                            </button>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
}

InvestmentCalculatorModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    investorData: PropTypes.shape({
        net_worth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        yearly_income: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        prior_reg_cf_amount: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onProceed: PropTypes.func.isRequired,
};

export default InvestmentCalculatorModal;
